import { useState, useMemo } from "react";
import moment from "jalali-moment";
import useFetch from "./useFetch";

/**
 * Everything the student home page needs, in one place.
 *
 * The four dashboard/* endpoints are purpose-built summary boxes, so the page
 * reads from those rather than pulling the full exam/assignment lists. Each was
 * previously fetched by a separate card component, which meant three
 * independent loading states and no way to reason across them - you could not
 * ask "what is the single most urgent thing?" because no component saw all the
 * data. Aggregating here is what makes the focus card possible.
 *
 * Response shapes (confirmed against the existing cards):
 *   student-panel/            -> { id, first_name, last_name, studentuser: {...} }
 *   dashboard/userinfo-box    -> { group_day, group_time }
 *   dashboard/classroom-box   -> { abscent_count, Total }
 *   dashboard/exam-box        -> [ nextExam, { average } ]        // average /100
 *   dashboard/assignment-box  -> [ [assignments], { average } ]   // average /100
 */

const DAY_MS = 24 * 60 * 60 * 1000;

const toNumber = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

/** Whole days from now until `date`; negative means the deadline has passed. */
const daysUntil = (date) => {
    if (!date) return null;
    const time = new Date(date).getTime();
    if (Number.isNaN(time)) return null;
    return Math.ceil((time - Date.now()) / DAY_MS);
};

const urgencyOf = (days) => {
    if (days === null) return "later";
    if (days < 0) return "overdue";
    if (days <= 1) return "today";
    if (days <= 3) return "soon";
    return "later";
};

export default function useStudentHome() {
    const [profile, setProfile] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const [classBox, setClassBox] = useState(null);
    const [examBox, setExamBox] = useState(null);
    const [assignmentBox, setAssignmentBox] = useState(null);

    const profileReq = useFetch({
        method: "GET",
    safeRead: true,
        url: "student-panel/",
        trigger: true,
        setter: setProfile,
    });
    const userReq = useFetch({
        method: "GET",
    safeRead: true,
        url: "dashboard/userinfo-box",
        trigger: true,
        setter: setUserInfo,
    });
    const classReq = useFetch({
        method: "GET",
    safeRead: true,
        url: "dashboard/classroom-box",
        trigger: true,
        setter: setClassBox,
    });
    const examReq = useFetch({
        method: "GET",
    safeRead: true,
        url: "dashboard/exam-box",
        trigger: true,
        setter: setExamBox,
    });
    const assignmentReq = useFetch({
        method: "GET",
    safeRead: true,
        url: "dashboard/assignment-box",
        trigger: true,
        setter: setAssignmentBox,
    });

    const student = useMemo(() => {
        const details = profile?.studentuser;
        return {
            firstName: profile?.first_name ?? "",
            fullName: [profile?.first_name, profile?.last_name]
                .filter(Boolean)
                .join(" "),
            // grade / type / group number, joined loosely because any of the
            // three can be missing on a given account.
            group: [
                details?.student_grade,
                details?.student_type,
                details?.student_gnum,
            ]
                .filter(Boolean)
                .join(" ")
                .trim(),
        };
    }, [profile]);

    const attendance = useMemo(() => {
        const absent = toNumber(classBox?.abscent_count);
        const total = toNumber(classBox?.Total);
        if (absent === null || !total) return null;
        const attended = Math.max(total - absent, 0);
        return {
            absent,
            total,
            attended,
            rate: Math.round((attended / total) * 100),
        };
    }, [classBox]);

    const deadlines = useMemo(() => {
        const list = Array.isArray(assignmentBox?.[0]) ? assignmentBox[0] : [];
        return list
            .map((item, index) => {
                const days = daysUntil(item?.assignment_available_time_end);
                return {
                    id: item?.id ?? `${item?.AssignmentName}-${index}`,
                    title: item?.assignment_headline || item?.AssignmentName,
                    subtitle: item?.assignment_headline
                        ? item?.AssignmentName
                        : "",
                    endsAt: item?.assignment_available_time_end,
                    daysLeft: days,
                    urgency: urgencyOf(days),
                };
            })
            .sort((a, b) => (a.daysLeft ?? 9e9) - (b.daysLeft ?? 9e9));
    }, [assignmentBox]);

    const nextExam = useMemo(() => {
        const raw = Array.isArray(examBox) ? examBox[0] : null;
        if (!raw?.exam_available_time_start) return null;
        return {
            startsAt: raw.exam_available_time_start,
            duration: raw.exam_duration,
            daysLeft: daysUntil(raw.exam_available_time_start),
        };
    }, [examBox]);

    /** Is the student's weekly class today? group_day is a Persian weekday name. */
    const classToday = useMemo(() => {
        if (!userInfo?.group_day) return false;
        const today = moment().locale("fa").format("dddd");
        return String(userInfo.group_day).includes(today);
    }, [userInfo]);

    /**
     * The single most important thing right now. Overdue and due-today
     * assignments outrank an upcoming exam; otherwise an exam inside a week
     * wins, because it is the less recoverable deadline.
     */
    const focus = useMemo(() => {
        const urgent = deadlines.find(
            (item) => item.urgency === "overdue" || item.urgency === "today"
        );
        if (urgent) return { type: "assignment", ...urgent };
        if (nextExam && nextExam.daysLeft !== null && nextExam.daysLeft <= 7) {
            return { type: "exam", ...nextExam };
        }
        if (deadlines.length > 0) {
            return { type: "assignment", ...deadlines[0] };
        }
        if (nextExam) return { type: "exam", ...nextExam };
        return null;
    }, [deadlines, nextExam]);

    return {
        loading:
            profileReq.loading ||
            userReq.loading ||
            classReq.loading ||
            examReq.loading ||
            assignmentReq.loading,
        student,
        schedule: {
            day: userInfo?.group_day ?? null,
            time: userInfo?.group_time ?? null,
            isToday: classToday,
        },
        attendance,
        examAverage: toNumber(examBox?.[1]?.average),
        assignmentAverage: toNumber(assignmentBox?.[1]?.average),
        deadlines,
        nextExam,
        focus,
    };
}
